/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask, lastValue } from 'ember-concurrency';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import ApiService, { ApiResponse } from 'cio/services/api';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import IdentityModel from 'cio/models/identity';
import EnvironmentService from 'cio/services/environment';
import { IdentityMapping, IdentityMappingArgs } from 'cio/utils/identity/types';

interface CredentialsConfigTwilioArgs {
  onLoad?: () => unknown;
  onErrorLoading?: () => unknown;
  onCreate?: () => Promise<unknown>;
  onDisable?: () => Promise<unknown>;
}

type Credential = {
  username?: string;
  password?: string;
  updated?: number;
};

type CredentialErrors = {
  username?: string[];
  password?: string[];
};

function isUnauthorized(err: ApiResponse) {
  if (!Array.isArray(err.errors)) {
    return false;
  }

  return err.errors[0].detail.includes('401 UNAUTHORIZED');
}

const AUTH_TOKEN_INVALID = 'Auth Token is invalid';
const ACCOUNT_SID_OR_AUTH_TOKEN_INVALID = 'Account SID and/or Auth Token is invalid';

export default class CredentialsConfigTwilio extends Component<CredentialsConfigTwilioArgs> {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  @tracked username = '';
  @tracked password = '';
  @tracked identitiesMap: IdentityMapping[] | null = null;
  @tracked errors!: CredentialErrors;
  @tracked isEditingToken = false;
  @tracked isEditingAccountSID = false;

  get onLoad() {
    return this.args.onLoad ?? (() => {});
  }

  get onErrorLoading() {
    return this.args.onErrorLoading ?? (() => {});
  }

  get onCreate() {
    return this.args.onCreate ?? (() => Promise.resolve());
  }

  get onDisable() {
    return this.args.onDisable ?? (() => Promise.resolve());
  }

  @action
  toggleEditing(value: boolean, type: 'token' | 'accountSID' = 'token') {
    this.isEditingToken = value;
    this.isEditingAccountSID = value && type === 'accountSID';
    this.setDefaults();
  }

  @action
  setUsername(username: string) {
    this.username = username;
  }

  @action
  setPassword(password: string) {
    this.password = password;
  }

  @action
  setIdentitiesMap(identitiesMap: IdentityMappingArgs) {
    this.identitiesMap = identitiesMap;
  }

  @action
  cancelMapping() {
    this.isEditingToken = false;
    this.isEditingAccountSID = false;
    this.setDefaults();
    this.setIdentitiesMap(null);
    this.identitiesForSwitch = null;
    this.needsMapping = false;
  }

  constructor(owner: unknown, args: CredentialsConfigTwilioArgs) {
    super(owner, args);

    this.loadCredential.perform();
    this.loadIdentitiesInUse.perform();
  }

  loadCredential = dropTask(async () => {
    let response: { credential?: Credential } = {};

    try {
      response = await this.api.request('credentials/twilio', {
        ignoreErrors: true,
        includeStatus: true,
      });

      this.onLoad();
    } catch (e) {
      if (e.status === '404') {
        this.onErrorLoading();
      }
    } finally {
      this.username = response.credential?.username ?? '';
    }

    return response.credential;
  });

  @lastValue('loadCredential') credential: Credential | null = null;

  createOrUpdateCredential = dropTask(async (evt?: Event) => {
    evt?.preventDefault();

    let { username, password } = this;

    try {
      await this.api.put('credentials/twilio', {
        data: { credential: { username, password } },
        ignoreErrors: true,
      });

      this.setDefaults();
      this.isEditingToken = false;

      await this.sync.perform();
      await this.onCreate();

      this.alerts.success('credential.enabled', 'Twilio credentials saved!');
    } catch (err) {
      if (isUnauthorized(err)) {
        if (this.credential) {
          this.errors = { password: [AUTH_TOKEN_INVALID] };
        } else {
          this.errors = {
            username: [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
            password: [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
          };
        }
      } else {
        this.apiErrors.report(err);
      }
    }
  });

  async performSwitchTask(evt: Event | undefined, apiPath: string, successMessage: string) {
    evt?.preventDefault();

    let { username, password } = this;
    let identities: { new_identities: IdentityModel[] } | null = null;

    try {
      identities = await this.api.put(apiPath, {
        data: { credential: { username, password }, identities: this.identitiesMap },
        ignoreErrors: true,
      });

      if (apiPath.includes('prepare_switch')) {
        this.identitiesForSwitch = identities?.new_identities ?? [];
        this.setDefaults(false);
        this.isEditingToken = false;
        this.isEditingAccountSID = false;
        if (this.identitiesInUse.length === 0) {
          successMessage = 'No mappings required, switching Twilio credentials.';
          this.needsMapping = false;
          this.setIdentitiesMap(null);
          this.performSwitchTask(evt, 'credentials/twilio/replace', 'Twilio credentials saved!');
        } else {
          this.needsMapping = true;
        }
      } else if (apiPath.includes('replace')) {
        this.cancelMapping();
        await this.sync.perform();
        await this.onCreate();
      }

      this.alerts.success('credential.switch', successMessage);
    } catch (err) {
      if (isUnauthorized(err)) {
        let errors = {
          username: [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
          password: this.credential ? [AUTH_TOKEN_INVALID] : [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
        };
        this.errors = errors;
      } else {
        this.apiErrors.report(err);
      }
    }

    return identities;
  }

  fetchIdentitiesForSwitch = dropTask(async (evt?: Event) => {
    return this.performSwitchTask(
      evt,
      'credentials/twilio/prepare_switch',
      'Please map your existing identities to the new Twilio identities.',
    );
  });

  @tracked identitiesForSwitch!: IdentityModel[] | null;
  @tracked needsMapping = false;

  saveIdentityTask = dropTask(async (evt?: Event) => {
    return this.performSwitchTask(evt, 'credentials/twilio/replace', 'Twilio credentials saved!');
  });

  disableCredential = dropTask(async () => {
    await this.api.delete('credentials/twilio');
    await this.reloadCredential();
    await this.onDisable();

    this.alerts.success('credential.disabled', 'Twilio credentials deleted!');
  });

  sync = dropTask(async () => {
    await this.api.post('twilio/sync', { data: {} });
    await this.reloadCredential();
  });

  loadIdentitiesInUse = dropTask(async () => {
    let identities = await this.store.query('identity', { type: 'twilio', in_use: true });
    return identities.map((identity) => identity.id);
  });

  @lastValue('loadIdentitiesInUse') identitiesInUse!: IdentityModel[];

  private async reloadCredential() {
    await this.environment.reload.perform();
    await this.loadCredential.perform();
    await this.loadIdentitiesInUse.perform();
  }

  private setDefaults(withPassword = true) {
    this.errors = {};
    if (withPassword) {
      this.password = '';
    }
  }
}
